import { useEffect, useState } from 'react';
import {
    styled,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ActionButton, CancelButton, SubmitButton, DeleteButton } from 'components/ui/buttons';
import { useNotification } from 'context/notification';
import {
    createProductGroup,
    deleteProductGroup,
    getProductGroups,
    updateProductGroup,
    updateProductGroupSort,
} from './ProductGroupsAPI';

export default function ProductGroups({ eventId, open, onClose }) {
    const [ticketGroups, setTicketGroups] = useState([]);
    const [orderedTicketGroups, setOrderedTicketGroups] = useState([]);
    const [view, setView] = useState('list');
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [groupName, setGroupName] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { notify } = useNotification();

    useEffect(() => {
        if (!open) {
            return;
        }

        setLoading(true);
        setLoadError(false);

        getProductGroups(eventId)
            .then((groups) => setTicketGroups(groups))
            .catch(() => setLoadError(true))
            .finally(() => setLoading(false));
    }, [eventId, open]);

    const returnToList = () => {
        setView('list');
        setSelectedGroup(null);
        setGroupName('');
        setOrderedTicketGroups([]);
    };

    const handleClose = () => {
        onClose();
        returnToList();
    };

    const handleAdd = () => {
        setSelectedGroup(null);
        setGroupName('');
        setView('add');
    };

    const handleEdit = (group) => {
        setSelectedGroup(group);
        setGroupName(group.prod_group_name);
        setView('edit');
    };

    const handleReorder = () => {
        setOrderedTicketGroups(ticketGroups);
        setView('reorder');
    };

    const handleReorderSave = async () => {
        setSubmitting(true);

        try {
            await updateProductGroupSort(
                eventId,
                orderedTicketGroups.map((group) => group.prod_group_id),
            );
            setTicketGroups(orderedTicketGroups);
            notify.success('Saved Order');
            returnToList();
        } catch {
            notify.error('Unable to save ticket group order');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSave = async () => {
        const name = groupName.trim();

        if (!name) {
            return;
        }

        let successMessage;

        setSubmitting(true);

        try {
            switch (view) {
                case 'edit':
                    await updateProductGroup(eventId, selectedGroup.prod_group_id, name);
                    successMessage = 'Ticket group updated';
                    break;
                case 'add':
                    await createProductGroup(eventId, name);
                    successMessage = 'Ticket group created';
                    break;
                case 'list':
                default:
                    return;
            }
        } catch {
            notify.error('Unable to save ticket group');
        } finally {
            setSubmitting(false);
        }

        if (!successMessage) {
            return;
        }

        notify.success(successMessage);
        setLoading(true);
        setLoadError(false);

        try {
            const groups = await getProductGroups(eventId);
            setTicketGroups(groups);
        } catch {
            setLoadError(true);
            notify.error('Unable to load ticket groups');
        } finally {
            setLoading(false);
        }

        returnToList();
    };

    const handleDelete = async () => {
        if (view !== 'edit') {
            return;
        }

        let deleted = false;

        setSubmitting(true);

        try {
            await deleteProductGroup(eventId, selectedGroup.prod_group_id);
            deleted = true;
        } catch {
            notify.error('Unable to delete ticket group');
        } finally {
            setSubmitting(false);
        }

        if (!deleted) {
            return;
        }

        notify.success('Ticket group deleted');
        setLoading(true);
        setLoadError(false);

        try {
            const groups = await getProductGroups(eventId);
            setTicketGroups(groups);
        } catch {
            setLoadError(true);
            notify.error('Unable to load ticket groups');
        } finally {
            setLoading(false);
        }

        returnToList();
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : handleClose} maxWidth="sm" fullWidth>
            {view === 'list' && (
                <ProductGroupsList
                    ticketGroups={ticketGroups}
                    onClose={handleClose}
                    onAdd={handleAdd}
                    onEdit={handleEdit}
                    onReorder={handleReorder}
                    loading={loading}
                    loadError={loadError}
                    submitting={submitting}
                />
            )}
            {view === 'add' && (
                <AddProductGroup
                    groupName={groupName}
                    onCancel={returnToList}
                    onNameChange={setGroupName}
                    onSave={handleSave}
                    submitting={submitting}
                />
            )}
            {view === 'edit' && (
                <EditProductGroup
                    groupName={groupName}
                    onCancel={returnToList}
                    onDelete={handleDelete}
                    onNameChange={setGroupName}
                    onSave={handleSave}
                    submitting={submitting}
                />
            )}
            {view === 'reorder' && (
                <ReorderProductGroups
                    ticketGroups={orderedTicketGroups}
                    onCancel={returnToList}
                    onOrderChange={setOrderedTicketGroups}
                    onSave={handleReorderSave}
                    submitting={submitting}
                />
            )}
        </Dialog>
    );
}

function ProductGroupsList({
    ticketGroups,
    loadError,
    loading,
    onClose,
    onAdd,
    onEdit,
    onReorder,
    submitting,
}) {
    let rows;

    if (loading) {
        rows = (
            <TableRow>
                <TableCell colSpan={3}>Loading ticket groups...</TableCell>
            </TableRow>
        );
    } else if (loadError) {
        rows = (
            <TableRow>
                <TableCell colSpan={3}>Unable to load ticket groups.</TableCell>
            </TableRow>
        );
    } else if (ticketGroups.length === 0) {
        rows = (
            <TableRow>
                <TableCell colSpan={3}>No ticket groups found.</TableCell>
            </TableRow>
        );
    } else {
        rows = ticketGroups.map((group) => (
            <TableRow key={group.prod_group_id}>
                <TableCell>{group.prod_group_name}</TableCell>
                <TableCell align="right">{group.product_count}</TableCell>
                <TableCell align="right">
                    <ActionButton size="small" onClick={() => onEdit(group)} disabled={submitting}>
                        Edit
                    </ActionButton>
                </TableCell>
            </TableRow>
        ));
    }

    return (
        <>
            <DialogTitle>Manage Ticket Groups</DialogTitle>
            <DialogContent dividers>
                <TableContainer>
                    <Table size="small" aria-label="Ticket groups">
                        <TableHead>
                            <TableRow>
                                <TableCell>Group</TableCell>
                                <TableCell align="right">Tickets</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>{rows}</TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <CancelButton onClick={onClose} disabled={submitting}>
                    Close
                </CancelButton>
                <ActionButton
                    onClick={onReorder}
                    disabled={loading || submitting || ticketGroups.length < 2}
                >
                    Reorder
                </ActionButton>
                <SubmitButton onClick={onAdd} disabled={loading || submitting}>
                    New Ticket Group
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function ReorderProductGroups({ ticketGroups, onCancel, onOrderChange, onSave, submitting }) {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = ticketGroups.findIndex((group) => group.prod_group_id === active.id);
        const newIndex = ticketGroups.findIndex((group) => group.prod_group_id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        onOrderChange(arrayMove(ticketGroups, oldIndex, newIndex));
    };

    return (
        <>
            <DialogTitle>Reorder Ticket Groups</DialogTitle>
            <DialogContent dividers>
                <TableContainer>
                    <Table size="small" aria-label="Reorder ticket groups">
                        <TableHead>
                            <TableRow>
                                <TableCell>Group</TableCell>
                            </TableRow>
                        </TableHead>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            modifiers={[restrictToVerticalAxis]}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={ticketGroups.map((group) => group.prod_group_id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <TableBody>
                                    {ticketGroups.map((group) => (
                                        <SortableProductGroup
                                            key={group.prod_group_id}
                                            group={group}
                                            disabled={submitting}
                                        />
                                    ))}
                                </TableBody>
                            </SortableContext>
                        </DndContext>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <CancelButton onClick={onCancel} disabled={submitting}>
                    Cancel
                </CancelButton>
                <SubmitButton onClick={onSave} disabled={submitting} loading={submitting}>
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function SortableProductGroup({ group, disabled }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: group.prod_group_id,
        disabled,
    });

    return (
        <TableRow
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition: isDragging ? undefined : transition,
            }}
            sx={(theme) => ({
                backgroundColor: isDragging ? theme.palette.grey[100] : '#fff',
                boxShadow: isDragging ? theme.shadows[3] : 'none',
                cursor: disabled ? 'default' : 'grab'
            })}
        >
            <TableCell sx={{ pl: 0.8 }} {...attributes} {...listeners}>
                <DragHandle
                    disabled={disabled}
                    aria-label={`Drag ${group.prod_group_name}`}
                >
                    <DragHandleIcon />
                </DragHandle>
                {group.prod_group_name}
            </TableCell>
        </TableRow>
    );
}

function AddProductGroup({ groupName, onCancel, onNameChange, onSave, submitting }) {
    return (
        <>
            <DialogTitle>Add Ticket Group</DialogTitle>
            <DialogContent dividers>
                <TextField
                    autoFocus
                    fullWidth
                    label="Group Name"
                    value={groupName}
                    onChange={(event) => onNameChange(event.target.value)}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <CancelButton onClick={onCancel} disabled={submitting}>
                    Cancel
                </CancelButton>
                <SubmitButton
                    onClick={onSave}
                    disabled={!groupName.trim() || submitting}
                    loading={submitting}
                >
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function EditProductGroup({ groupName, onCancel, onDelete, onNameChange, onSave, submitting }) {
    return (
        <>
            <DialogTitle>Edit Ticket Group</DialogTitle>
            <DialogContent dividers>
                <TextField
                    autoFocus
                    fullWidth
                    label="Group Name"
                    value={groupName}
                    onChange={(event) => onNameChange(event.target.value)}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={submitting}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onCancel} disabled={submitting}>
                    Cancel
                </CancelButton>
                <SubmitButton
                    onClick={onSave}
                    disabled={!groupName.trim() || submitting}
                    loading={submitting}
                >
                    Save
                </SubmitButton>
            </DialogActions>
        </>
    );
}

const DragHandle = styled(IconButton)(({ theme }) => ({
    color: theme.palette.grey[500],
    cursor: 'grab',
    flexShrink: 0,
    touchAction: 'none',
    marginRight: theme.spacing(1)
}));

DragHandle.defaultProps = { size: 'small', disableRipple: true };

const DragHandleIcon = styled(DragIndicatorIcon)(({ theme }) => ({
    color: theme.palette.grey[400],
}));
